import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import {
  PlayIcon,
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, schemaApi, queryBuilderApi } from '@/lib/api';
import { parseBuilderSql, parsedQuerySignature, type ParsedBuilderQuery } from '@/lib/parseBuilderSql';
import toast from 'react-hot-toast';
import clsx from 'clsx';

interface QueryCondition {
  id: string;
  column: string;
  operator: string;
  value: string;
}

interface SortField {
  id: string;
  column: string;
  ascending: boolean;
}

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable?: boolean;
  is_primary_key?: boolean;
}

type EditorKind = 'boolean' | 'number' | 'date' | 'datetime' | 'json' | 'text';

interface SchemaTable {
  table_name?: string;
  name?: string;
  columns?: ColumnInfo[];
}

const OPERATORS: Array<{ value: string; label: string; symbol: string }> = [
  { value: 'eq', label: 'equals', symbol: '=' },
  { value: 'neq', label: 'not equals', symbol: '!=' },
  { value: 'gt', label: 'greater than', symbol: '>' },
  { value: 'gte', label: 'greater or equal', symbol: '>=' },
  { value: 'lt', label: 'less than', symbol: '<' },
  { value: 'lte', label: 'less or equal', symbol: '<=' },
  { value: 'like', label: 'like', symbol: 'LIKE' },
  { value: 'ilike', label: 'like, ignore case', symbol: 'ILIKE' },
  { value: 'in', label: 'in list', symbol: 'IN' },
  { value: 'is', label: 'is', symbol: 'IS' },
];

const PAGE_SIZES = [10, 25, 50, 100];

function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function sqlLiteral(value: string): string {
  if (value === '') return "''";
  if (/^-?\d+(\.\d+)?$/.test(value)) return value;
  if (value === 'true' || value === 'false') return value.toUpperCase();
  if (value === 'null') return 'NULL';
  return `'${value.replace(/'/g, "''")}'`;
}

function conditionSql(condition: QueryCondition): string | null {
  if (!condition.column) return null;
  const column = quoteIdent(condition.column);
  const operator = OPERATORS.find((item) => item.value === condition.operator);
  const symbol = operator?.symbol ?? '=';

  if (condition.operator === 'is') {
    const token = condition.value.trim().toLowerCase();
    if (token === '' || token === 'null') return `${column} IS NULL`;
    if (token === 'true') return `${column} IS TRUE`;
    if (token === 'false') return `${column} IS FALSE`;
    return `${column} IS ${sqlLiteral(condition.value)}`;
  }

  if (condition.operator === 'in') {
    const items = condition.value.split(',').map((item) => item.trim()).filter(Boolean);
    if (items.length === 0) return null;
    return `${column} IN (${items.map(sqlLiteral).join(', ')})`;
  }

  return `${column} ${symbol} ${sqlLiteral(condition.value)}`;
}

function conditionPayload(condition: QueryCondition): { column: string; operator: string; value: unknown } | null {
  if (!condition.column) return null;
  if (condition.operator === 'is') {
    const token = condition.value.trim().toLowerCase();
    const value = token === '' || token === 'null' ? null : token === 'true' ? true : token === 'false' ? false : condition.value;
    return { column: condition.column, operator: 'is', value };
  }
  if (condition.operator === 'in') {
    const items = condition.value.split(',').map((item) => item.trim()).filter(Boolean);
    if (items.length === 0) return null;
    return { column: condition.column, operator: 'in', value: items };
  }
  return { column: condition.column, operator: condition.operator, value: condition.value };
}

function buildPreview(
  table: string,
  fields: string[],
  conditions: QueryCondition[],
  sorts: SortField[],
  limit: number | undefined
): string {
  if (!table) return '';
  const selected = fields.includes('*') || fields.length === 0
    ? '*'
    : fields.map(quoteIdent).join(', ');
  const clauses = conditions.map(conditionSql).filter((clause): clause is string => Boolean(clause));
  const order = sorts
    .filter((sort) => sort.column)
    .map((sort) => `${quoteIdent(sort.column)} ${sort.ascending ? 'ASC' : 'DESC'}`);

  let sql = `SELECT ${selected}\nFROM ${quoteIdent(table)}`;
  if (clauses.length > 0) sql += `\nWHERE ${clauses.join('\n  AND ')}`;
  if (order.length > 0) sql += `\nORDER BY ${order.join(', ')}`;
  if (limit && limit > 0) sql += `\nLIMIT ${limit}`;
  return `${sql};`;
}

function cellText(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function previewText(value: unknown): string {
  const text = cellText(value);
  if (text.length <= 50) return text;
  return `${text.slice(0, 50)}…`;
}

function editorKind(dataType: string): EditorKind {
  const type = dataType.toLowerCase();
  if (type === 'boolean') return 'boolean';
  if (['smallint', 'integer', 'bigint', 'numeric', 'decimal', 'real', 'double precision', 'money'].some((name) => type.includes(name))) {
    return 'number';
  }
  if (type === 'date') return 'date';
  if (type.includes('timestamp')) return 'datetime';
  if (type === 'json' || type === 'jsonb') return 'json';
  return 'text';
}

function canonical(value: unknown, dataType: string): string {
  if (value == null) return '';
  const kind = editorKind(dataType);
  if (kind === 'date' || kind === 'datetime') {
    const parsed = new Date(String(value));
    if (!Number.isNaN(parsed.getTime())) {
      const pad = (part: number) => String(part).padStart(2, '0');
      const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
      if (kind === 'date') return date;
      return `${date}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    }
  }
  if (kind === 'json' && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function sameValue(original: unknown, next: unknown): boolean {
  if (Object.is(original, next)) return true;
  if (original == null && next == null) return true;
  if (original instanceof Date || next instanceof Date) {
    return canonical(original, 'timestamp') === canonical(next, 'timestamp');
  }
  if (typeof original === 'number' || typeof next === 'number') {
    const left = Number(original);
    const right = Number(next);
    return original != null && next != null && !Number.isNaN(left) && left === right;
  }
  if (typeof original === 'boolean' || typeof next === 'boolean') {
    return String(original) === String(next);
  }
  return cellText(original) === cellText(next);
}

function tableFromSql(sql: string): string {
  const quoted = sql.match(/\bfrom\s+"([a-zA-Z_][a-zA-Z0-9_]*)"/i);
  if (quoted) return quoted[1];
  const plain = sql.match(/\bfrom\s+([a-zA-Z_][a-zA-Z0-9_]*)/i);
  return plain?.[1] ?? '';
}

function CellEditor({
  value,
  column,
  onCommit,
  onCancel,
}: {
  value: unknown;
  column: ColumnInfo;
  onCommit: (next: unknown) => void;
  onCancel: () => void;
}) {
  const kind = editorKind(column.data_type);
  const initial = kind === 'boolean' ? value === true : canonical(value, column.data_type);
  const [local, setLocal] = useState(kind === 'boolean' ? value === true : initial);
  const cancelled = useRef(false);
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    fieldRef.current?.focus();
    if (fieldRef.current instanceof HTMLInputElement && kind === 'text') {
      fieldRef.current.select();
    }
  }, [kind]);

  const commitFrom = (raw: unknown) => {
    let next: unknown = raw;
    if (kind === 'number') {
      const text = String(raw);
      next = text === '' ? (column.is_nullable ? null : '') : Number(text);
    } else if (kind === 'boolean') {
      next = raw === true;
    } else if (kind === 'json') {
      const text = String(raw);
      if (text === '') {
        next = null;
      } else {
        try {
          next = JSON.parse(text);
        } catch {
          toast.error('That cell is not valid JSON');
          return;
        }
      }
    } else if (String(raw) === '' && (value == null || column.is_nullable)) {
      next = null;
    }
    onCommit(next);
  };

  if (kind === 'boolean') {
    return (
      <input
        data-cell-editor=""
        ref={fieldRef as RefObject<HTMLInputElement>}
        type="checkbox"
        checked={local === true}
        aria-label={`Edit ${column.column_name}`}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        onChange={(event) => onCommit(event.target.checked)}
        onBlur={(event) => onCommit(event.currentTarget.checked)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
      />
    );
  }

  const finish = () => {
    if (!cancelled.current) commitFrom(local);
  };

  const common = {
    'data-cell-editor': '',
    value: String(local),
    'aria-label': `Edit ${column.column_name}`,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLocal(event.target.value),
    onBlur: finish,
    onKeyDown: (event: ReactKeyboardEvent) => {
      if (event.key === 'Enter' && kind !== 'json') {
        event.preventDefault();
        cancelled.current = true;
        commitFrom(local);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelled.current = true;
        onCancel();
      }
    },
    className: 'form-input w-full py-1 text-sm',
  };

  if (kind === 'json') {
    return <textarea {...common} ref={fieldRef as RefObject<HTMLTextAreaElement>} rows={3} />;
  }

  const inputType = kind === 'number' ? 'number' : kind === 'date' ? 'date' : kind === 'datetime' ? 'datetime-local' : 'text';
  return <input {...common} ref={fieldRef as RefObject<HTMLInputElement>} type={inputType} step={kind === 'number' ? 'any' : undefined} />;
}

function tableName(table: SchemaTable): string {
  return table.table_name || table.name || '';
}

export default function QueryBuilderPage() {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [sqlDraft, setSqlDraft] = useState('');
  const [sqlDirty, setSqlDirty] = useState(false);
  const [queryResults, setQueryResults] = useState<{ data?: Record<string, unknown>[]; executionTime?: number; rowCount?: number } | null>(null);
  const [resultTable, setResultTable] = useState('');
  const [drafts, setDrafts] = useState<Record<number, Record<string, unknown>>>({});
  const [editing, setEditing] = useState<{ row: number; column: string } | null>(null);
  const draftsRef = useRef<Record<number, Record<string, unknown>>>({});
  const [runError, setRunError] = useState<string | null>(null);
  const [resultsPage, setResultsPage] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(25);

  const [selectedTable, setSelectedTable] = useState('');
  const [selectFields, setSelectFields] = useState<string[]>(['*']);
  const [conditions, setConditions] = useState<QueryCondition[]>([]);
  const [sorts, setSorts] = useState<SortField[]>([]);
  const [limitInput, setLimitInput] = useState('');
  const [builderTab, setBuilderTab] = useState<'table' | 'columns' | 'filters' | 'sort'>('table');
  const appliedSqlSignature = useRef('');

  const runRef = useRef<() => void>(() => {});

  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    { enabled: isAuthenticated && !!projectId }
  );

  const { data: schemaResponse, isLoading: schemaLoading } = useQuery(
    ['schema', projectId],
    () => schemaApi.getSchema(projectId as string),
    { enabled: isAuthenticated && !!projectId }
  );

  const limitValue = /^\d+$/.test(limitInput) ? parseInt(limitInput, 10) : undefined;
  const previewSql = useMemo(
    () => buildPreview(selectedTable, selectFields, conditions, sorts, limitValue),
    [selectedTable, selectFields, conditions, sorts, limitValue]
  );
  const displayedSql = sqlDirty ? sqlDraft : previewSql;

  const rememberDrafts = (next: Record<number, Record<string, unknown>>) => {
    draftsRef.current = next;
    setDrafts(next);
  };

  const commitCell = (rowIndex: number, column: string, next: unknown) => {
    const original = queryResults?.data?.[rowIndex]?.[column];
    const current = { ...draftsRef.current };
    const rowDraft = { ...(current[rowIndex] ?? {}) };
    if (sameValue(original, next)) {
      delete rowDraft[column];
    } else {
      rowDraft[column] = next;
    }
    if (Object.keys(rowDraft).length === 0) {
      delete current[rowIndex];
    } else {
      current[rowIndex] = rowDraft;
    }
    rememberDrafts(current);
    setEditing(null);
  };

  const runMutation = useMutation(async () => {
    if (!projectId) throw new Error('Project is missing');
    const table = sqlDirty ? (tableFromSql(displayedSql) || selectedTable) : selectedTable;
    if (sqlDirty) {
      const result = await queryBuilderApi.executeQuery(projectId, displayedSql, []);
      return { result, table };
    }
    const where = conditions
      .map(conditionPayload)
      .filter((item): item is NonNullable<ReturnType<typeof conditionPayload>> => item !== null);
    const built = await queryBuilderApi.buildQuery(projectId, {
      table: selectedTable,
      select: selectFields.includes('*') || selectFields.length === 0 ? ['*'] : selectFields,
      where,
      orderBy: sorts.filter((sort) => sort.column).map((sort) => ({ column: sort.column, ascending: sort.ascending })),
      limit: limitValue,
    });
    if (!built?.query) {
      throw new Error(built?.message || 'Failed to build query');
    }
    const result = await queryBuilderApi.executeQuery(projectId, built.query, built.params || []);
    return { result, table };
  }, {
    onSuccess: ({ result, table }) => {
      setRunError(null);
      setResultTable(table);
      rememberDrafts({});
      setEditing(null);
      setQueryResults(result);
      setResultsPage(0);
      const count = Array.isArray(result?.data) ? result.data.length : 0;
      toast.success(`${count} row${count === 1 ? '' : 's'} in ${result?.executionTime ?? 0}ms`);
    },
    onError: (error: unknown) => {
      const response = error as { response?: { data?: { message?: string } }; message?: string };
      const message = response.response?.data?.message || response.message || 'Query failed';
      setRunError(message);
      toast.error(message);
    },
  });

  const saveMutation = useMutation(async () => {
    if (!projectId || !resultTable) {
      throw new Error('Run a query against one table before saving edits');
    }
    const schema = schemaResponse?.data?.schema || schemaResponse?.data;
    const schemaTables: SchemaTable[] = Array.isArray(schema?.tables) ? schema.tables : [];
    const table = schemaTables.find((item) => tableName(item) === resultTable);
    const primaryKeys = (table?.columns ?? []).filter((column) => column.is_primary_key).map((column) => column.column_name);
    const pending = draftsRef.current;
    const rows = queryResults?.data ?? [];
    const saved: number[] = [];
    let failure = '';

    for (const [indexText, changes] of Object.entries(pending)) {
      const index = Number(indexText);
      const original = rows[index];
      if (!original || Object.keys(changes).length === 0) continue;
      const whereColumns = primaryKeys.length > 0 ? primaryKeys : Object.keys(original);
      if (primaryKeys.some((column) => !(column in original))) {
        failure = 'Include the primary key in the results before saving';
        break;
      }
      const params: unknown[] = [];
      const sets = Object.keys(changes).map((column) => {
        params.push(changes[column]);
        return `${quoteIdent(column)} = $${params.length}`;
      });
      const where = whereColumns.map((column) => {
        if (original[column] == null) return `${quoteIdent(column)} IS NULL`;
        params.push(original[column]);
        return `${quoteIdent(column)} = $${params.length}`;
      });
      const sql = `UPDATE ${quoteIdent(resultTable)} SET ${sets.join(', ')} WHERE ${where.join(' AND ')}`;
      try {
        const response = await queryBuilderApi.executeQuery(projectId, sql, params);
        if (!response?.rowCount) {
          failure = 'A row no longer matches, so it was not saved';
          break;
        }
        saved.push(index);
      } catch (error) {
        const response = error as { response?: { data?: { message?: string } }; message?: string };
        failure = response.response?.data?.message || response.message || 'Save failed';
        break;
      }
    }

    return { saved, failure, pending, rows };
  }, {
    onSuccess: ({ saved, failure, pending, rows }) => {
      if (saved.length > 0) {
        const data = rows.map((row, index) => (
          pending[index] && saved.includes(index) ? { ...row, ...pending[index] } : row
        ));
        setQueryResults((current) => current ? { ...current, data } : current);
        const remaining = { ...pending };
        saved.forEach((index) => {
          delete remaining[index];
        });
        rememberDrafts(remaining);
      }
      if (failure) {
        toast.error(failure);
      } else {
        toast.success(saved.length === 1 ? 'Saved 1 row' : `Saved ${saved.length} rows`);
      }
    },
  });

  runRef.current = () => {
    if (!displayedSql.trim()) {
      toast.error('Choose a table or write a query');
      return;
    }
    if (!sqlDirty && !selectedTable) {
      toast.error('Choose a table or edit the SQL');
      return;
    }
    setRunError(null);
    runMutation.mutate();
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[data-cell-editor]')) return;
        event.preventDefault();
        runRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (authLoading) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading…</div>;
  }

  if (!isAuthenticated) return null;

  const project = projectResponse?.data?.project;
  const schemaData = schemaResponse?.data?.schema || schemaResponse?.data;
  const tables: SchemaTable[] = Array.isArray(schemaData?.tables) ? schemaData.tables : [];
  const selectedTableData = tables.find((table) => tableName(table) === selectedTable);
  const columns: ColumnInfo[] = Array.isArray(selectedTableData?.columns) ? selectedTableData.columns : [];
  const allColumns = selectFields.includes('*');

  const allRows = queryResults?.data || [];
  const totalRows = allRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / resultsPerPage));
  const startIdx = resultsPage * resultsPerPage;
  const endIdx = Math.min(startIdx + resultsPerPage, totalRows);
  const pageRows = allRows.slice(startIdx, endIdx);
  const resultColumns = tables.find((table) => tableName(table) === resultTable)?.columns ?? [];
  const primaryKeys = resultColumns.filter((column) => column.is_primary_key).map((column) => column.column_name);
  const resultKeys = allRows[0] ? Object.keys(allRows[0]) : [];
  const canEdit = Boolean(resultTable) && (primaryKeys.length === 0 || primaryKeys.every((key) => resultKeys.includes(key)));
  const dirtyCount = Object.keys(drafts).length;
  const parsedDraft = sqlDirty ? parseBuilderSql(sqlDraft) : null;
  const sqlHasFrom = /^\s*select\b[\s\S]*\bfrom\b/i.test(sqlDraft);

  const resetSql = () => {
    setSqlDirty(false);
    setSqlDraft('');
    appliedSqlSignature.current = '';
  };

  const editFromBuilder = () => {
    setSqlDirty(false);
    appliedSqlSignature.current = '';
  };

  const applyParsedQuery = (parsed: ParsedBuilderQuery) => {
    const signature = parsedQuerySignature(parsed);
    if (signature === appliedSqlSignature.current) return;
    appliedSqlSignature.current = signature;
    setSelectedTable(parsed.table);
    setSelectFields(parsed.fields);
    setConditions(parsed.conditions.map((condition) => ({ ...condition, id: crypto.randomUUID() })));
    setSorts(parsed.sorts.map((sort) => ({ ...sort, id: crypto.randomUUID() })));
    setLimitInput(parsed.limit ? String(parsed.limit) : '');
  };

  const chooseTable = (name: string) => {
    setSelectedTable(name);
    setSelectFields(['*']);
    setConditions([]);
    setSorts([]);
    resetSql();
  };

  const toggleColumn = (name: string) => {
    editFromBuilder();
    if (allColumns) {
      setSelectFields([name]);
      return;
    }
    if (selectFields.includes(name)) {
      const next = selectFields.filter((field) => field !== name);
      setSelectFields(next.length === 0 ? ['*'] : next);
      return;
    }
    setSelectFields([...selectFields, name]);
  };

  const updateCondition = (id: string, patch: Partial<QueryCondition>) => {
    editFromBuilder();
    setConditions((current) => current.map((condition) => (
      condition.id === id ? { ...condition, ...patch } : condition
    )));
  };

  return (
    <div className="px-4 pb-24 sm:px-0 sm:pb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Query builder</h1>
          <p className="mt-1 text-sm text-gray-500">
            {project?.name || 'Project'} · Filters, sort, and SQL stay on one screen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => runRef.current()}
          disabled={runMutation.isLoading}
          className="btn-primary hidden sm:inline-flex disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlayIcon className="mr-2 h-4 w-4" />
          {runMutation.isLoading ? 'Running…' : 'Run query'}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 items-stretch gap-6 xl:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)]">
        <section className="flex h-full flex-col rounded-lg border border-gray-200 bg-white">
          <div role="tablist" aria-label="Query builder" className="flex gap-1 border-b border-gray-200 px-2 pt-2">
            {([
              ['table', 'Table', 0],
              ['columns', 'Columns', allColumns ? 0 : selectFields.length],
              ['filters', 'Filters', conditions.length],
              ['sort', 'Sort', sorts.length],
            ] as const).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={builderTab === id}
                onClick={() => setBuilderTab(id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium',
                  builderTab === id
                    ? 'bg-white text-primary-700 shadow-[inset_0_-2px_0_0] shadow-primary-600'
                    : 'text-gray-500 hover:text-gray-800'
                )}
              >
                {label}
                {count > 0 && (
                  <span className={clsx(
                    'rounded-full px-1.5 text-xs',
                    builderTab === id ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-500'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="min-h-[14rem] flex-1 overflow-auto p-4">
          {builderTab === 'table' && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Table</h2>
              <span className="text-xs text-gray-400">
                {schemaLoading ? 'Loading…' : `${tables.length} table${tables.length === 1 ? '' : 's'}`}
              </span>
            </div>
            <select
              value={selectedTable}
              onChange={(event) => chooseTable(event.target.value)}
              className="form-select"
              aria-label="Table"
            >
              <option value="">{schemaLoading ? 'Loading tables…' : 'Choose a table'}</option>
              {tables.map((table) => {
                const name = tableName(table);
                return (
                  <option key={name} value={name}>
                    {name}{table.columns ? ` · ${table.columns.length} columns` : ''}
                  </option>
                );
              })}
            </select>
            {!schemaLoading && tables.length === 0 && (
              <p className="mt-3 text-sm text-gray-500">This project has no tables yet. Create one from Schema, then come back.</p>
            )}
          </div>
          )}

          {builderTab === 'columns' && (
          <div>
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Columns</h2>
            {columns.length === 0 ? (
              <p className="text-sm text-gray-500">
                {selectedTable ? 'This table has no columns.' : 'Choose a table to pick columns.'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    editFromBuilder();
                    setSelectFields(['*']);
                  }}
                  className={clsx(
                    'rounded-full border px-3 py-1 text-sm',
                    allColumns
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  )}
                >
                  All columns
                </button>
                {columns.map((column) => {
                  const selected = allColumns || selectFields.includes(column.column_name);
                  return (
                    <button
                      key={column.column_name}
                      type="button"
                      onClick={() => toggleColumn(column.column_name)}
                      className={clsx(
                        'rounded-full border px-3 py-1 text-left text-sm',
                        selected && !allColumns
                          ? 'border-primary-600 bg-primary-50 text-primary-800'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      {column.column_name}
                      <span className="ml-1.5 text-xs text-gray-400">{column.data_type}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {builderTab === 'filters' && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={() => {
                  editFromBuilder();
                  setConditions((current) => [...current, { id: crypto.randomUUID(), column: columns[0]?.column_name || '', operator: 'eq', value: '' }]);
                }}
                className="btn-secondary btn-sm"
                disabled={!selectedTable}
              >
                <PlusIcon className="mr-1 h-3.5 w-3.5" />
                Add
              </button>
            </div>
            {conditions.length === 0 ? (
              <p className="text-sm text-gray-500">No filters. The query returns every matching row.</p>
            ) : (
              <div className="space-y-3">
                {conditions.map((condition, index) => (
                  <div key={condition.id} className="rounded-md border border-gray-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        {index === 0 ? 'Where' : 'And'}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          editFromBuilder();
                          setConditions((current) => current.filter((item) => item.id !== condition.id));
                        }}
                        className="text-gray-400 hover:text-red-600"
                        aria-label="Remove filter"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <select
                        value={condition.column}
                        onChange={(event) => updateCondition(condition.id, { column: event.target.value })}
                        className="form-select"
                        aria-label="Filter column"
                      >
                        <option value="">Column</option>
                        {columns.map((column) => (
                          <option key={column.column_name} value={column.column_name}>
                            {column.column_name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={condition.operator}
                        onChange={(event) => updateCondition(condition.id, { operator: event.target.value })}
                        className="form-select"
                        aria-label="Filter operator"
                      >
                        {OPERATORS.map((operator) => (
                          <option key={operator.value} value={operator.value}>{operator.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={condition.value}
                        onChange={(event) => updateCondition(condition.id, { value: event.target.value })}
                        placeholder={condition.operator === 'in' ? 'a, b, c' : condition.operator === 'is' ? 'null, true, or false' : 'Value'}
                        className="form-input sm:col-span-2"
                        aria-label="Filter value"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {builderTab === 'sort' && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Sort and limit</h2>
              <button
                type="button"
                onClick={() => {
                  editFromBuilder();
                  setSorts((current) => [...current, { id: crypto.randomUUID(), column: columns[0]?.column_name || '', ascending: true }]);
                }}
                className="btn-secondary btn-sm"
                disabled={!selectedTable}
              >
                <PlusIcon className="mr-1 h-3.5 w-3.5" />
                Sort
              </button>
            </div>
            <div className="space-y-2">
              {sorts.map((sort) => (
                <div key={sort.id} className="flex items-center gap-2">
                  <select
                    value={sort.column}
                    onChange={(event) => {
                      editFromBuilder();
                      setSorts((current) => current.map((item) => item.id === sort.id ? { ...item, column: event.target.value } : item));
                    }}
                    className="form-select"
                    aria-label="Sort column"
                  >
                    <option value="">Column</option>
                    {columns.map((column) => (
                      <option key={column.column_name} value={column.column_name}>{column.column_name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      editFromBuilder();
                      setSorts((current) => current.map((item) => item.id === sort.id ? { ...item, ascending: !item.ascending } : item));
                    }}
                    className="btn-secondary btn-sm shrink-0"
                  >
                    {sort.ascending ? 'Asc' : 'Desc'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      editFromBuilder();
                      setSorts((current) => current.filter((item) => item.id !== sort.id));
                    }}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Remove sort"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <label className="mt-4 block">
              <span className="form-label">Row limit</span>
              <input
                type="number"
                min={1}
                value={limitInput}
                onChange={(event) => {
                  editFromBuilder();
                  setLimitInput(event.target.value);
                }}
                placeholder="No limit"
                className="form-input"
              />
            </label>
          </div>
          )}
          </div>
        </section>

        <div className="space-y-4">
          <section className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">SQL</h2>
                <p className="text-xs text-gray-500">Edit the form or the SQL. A valid select updates the other side. Press ⌘ Enter or Ctrl Enter to run.</p>
              </div>
              <div className="flex items-center gap-2">
                {sqlDirty && (
                  <button type="button" onClick={resetSql} className="btn-secondary btn-sm">
                    <ArrowPathIcon className="mr-1 h-3.5 w-3.5" />
                    {parsedDraft ? 'Format SQL' : 'Revert SQL'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (!displayedSql) return;
                    navigator.clipboard.writeText(displayedSql).then(
                      () => toast.success('SQL copied'),
                      () => toast.error('Could not copy SQL')
                    );
                  }}
                  className="btn-secondary btn-sm"
                  disabled={!displayedSql}
                >
                  <ClipboardDocumentIcon className="mr-1 h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
            </div>
            <div className="relative min-h-[14rem] flex-1">
              <textarea
                value={displayedSql}
                onChange={(event) => {
                  const next = event.target.value;
                  setSqlDirty(true);
                  setSqlDraft(next);
                  const parsed = parseBuilderSql(next);
                  if (parsed) applyParsedQuery(parsed);
                }}
                onBlur={() => {
                  if (parseBuilderSql(sqlDraft)) resetSql();
                }}
                placeholder="Choose a table, or write SQL here."
                spellCheck={false}
                className="form-textarea h-full min-h-[14rem] resize-y pb-12 font-mono text-sm leading-6"
                aria-label="SQL"
              />
              {sqlDirty && (parsedDraft || sqlHasFrom) && (
                <p className={clsx(
                  'pointer-events-none absolute inset-x-2 bottom-2 rounded-md px-3 py-1.5 text-xs',
                  parsedDraft ? 'bg-primary-50 text-primary-800' : 'bg-amber-50 text-amber-800'
                )}>
                  {parsedDraft
                    ? 'The form matches this SQL. Change the form and this statement is rewritten.'
                    : 'This SQL is more than a single filtered select, so the form was left unchanged.'}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>

      <section className="mt-6 min-w-0 max-w-full rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Results</h2>
                {totalRows > 0 && (
                  <p className="text-xs text-gray-500">
                    {canEdit ? 'Double-click a cell to edit it. Changed rows are saved together.' : 'Include the primary key in the results to edit rows.'}
                  </p>
                )}
              </div>
              {queryResults && (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>{totalRows} row{totalRows === 1 ? '' : 's'}</span>
                  <span>{queryResults.executionTime ?? 0} ms</span>
                  <select
                    value={resultsPerPage}
                    onChange={(event) => {
                      setResultsPerPage(Number(event.target.value));
                      setResultsPage(0);
                    }}
                    className="form-select w-auto py-1 text-sm"
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>{size} / page</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {runError && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {runError}
              </div>
            )}

            {!queryResults && !runError && (
              <p className="text-sm text-gray-500">Run a query to see rows here. They stay on this page while you change the builder.</p>
            )}

            {queryResults && totalRows === 0 && (
              <p className="text-sm text-gray-500">The query finished and returned no rows.</p>
            )}

            {totalRows > 0 && (
              <>
                <div className="max-w-full overflow-x-auto rounded-md border border-gray-200">
                  <table className="w-max min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        {Object.keys(allRows[0]).map((key) => (
                          <th key={key} className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {pageRows.map((row, index) => {
                        const rowIndex = startIdx + index;
                        const rowDraft = drafts[rowIndex];
                        const rowDirty = Boolean(rowDraft && Object.keys(rowDraft).length > 0);
                        return (
                          <tr key={rowIndex} className={clsx(rowDirty ? 'bg-amber-50' : 'hover:bg-primary-50/40')}>
                            {Object.keys(allRows[0]).map((key) => {
                              const value = rowDraft && key in rowDraft ? rowDraft[key] : row[key];
                              const columnInfo = resultColumns.find((column) => column.column_name === key);
                              const isEditing = editing?.row === rowIndex && editing.column === key;
                              return (
                                <td
                                  key={key}
                                  className="whitespace-nowrap px-3 py-2 text-gray-900"
                                  title={value == null ? 'NULL' : cellText(value)}
                                  onDoubleClick={() => {
                                    if (!canEdit) {
                                      toast.error(resultTable ? 'Include the primary key in the results to edit rows' : 'Run a query against one table to edit rows');
                                      return;
                                    }
                                    if (!columnInfo) {
                                      toast.error('This column cannot be edited');
                                      return;
                                    }
                                    setEditing({ row: rowIndex, column: key });
                                  }}
                                >
                                  {isEditing && columnInfo ? (
                                    <CellEditor
                                      value={value}
                                      column={columnInfo}
                                      onCommit={(next) => commitCell(rowIndex, key, next)}
                                      onCancel={() => setEditing(null)}
                                    />
                                  ) : value == null ? (
                                    <span className="text-gray-400">NULL</span>
                                  ) : (
                                    previewText(value)
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
                    <p>Showing {startIdx + 1}–{endIdx} of {totalRows}</p>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => setResultsPage(resultsPage - 1)} disabled={resultsPage === 0} className="rounded border border-gray-300 p-1 hover:bg-gray-50 disabled:opacity-40" aria-label="Previous page">
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <span className="px-2">Page {resultsPage + 1} of {totalPages}</span>
                      <button type="button" onClick={() => setResultsPage(resultsPage + 1)} disabled={resultsPage >= totalPages - 1} className="rounded border border-gray-300 p-1 hover:bg-gray-50 disabled:opacity-40" aria-label="Next page">
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
      </section>

      {dirtyCount > 0 && (
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isLoading}
          className="btn-primary fixed bottom-20 right-4 z-30 shadow-lg disabled:cursor-not-allowed disabled:opacity-50 sm:bottom-6 sm:right-8"
        >
          {saveMutation.isLoading ? 'Saving…' : `Save ${dirtyCount} row${dirtyCount === 1 ? '' : 's'}`}
        </button>
      )}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 p-3 backdrop-blur sm:hidden">
        <button
          type="button"
          onClick={() => runRef.current()}
          disabled={runMutation.isLoading}
          className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlayIcon className="mr-2 h-4 w-4" />
          {runMutation.isLoading ? 'Running…' : 'Run query'}
        </button>
      </div>
    </div>
  );
}
